<template>
  <div>
    <b-row class="match-height">
      <b-col cols="6">
        <report-table-card
          class="portfolio-card-with-hint"
          title="Performance Summary"
          explanation="Latest performance and return data across all products"
          table-header
          :fields="performanceSummaryFields"
          :items="performanceSummaryItems"
          :loading="!portfolioData"
          :show-info-icon="portfolioType !== 'u'"
          @onShowProducts="$emit('show-products', $event)"
        >
          <template #hint>
            <hint-popup
              :id="22"
              class="portfolio-card-hint-popup"
            />
          </template>
        </report-table-card>
      </b-col>

      <b-col cols="6">
        <portfolio-evolution-chart
          v-if="portfolioType === 'u'"
          class="portfolio-card-with-hint"
          :portfolio-type="portfolioType"
          :evolution-data="evolutionData"
          :index-underlying-vol-ratio="indexUnderlyingVolRatio"
          :loading="loading"
          @show-index-selector="$emit('show-index-selector')"
        >
          <template #hint>
            <hint-popup
              :id="28"
              class="portfolio-card-hint-popup"
            />
          </template>
        </portfolio-evolution-chart>
        <portfolio-history-chart
          v-else
          class="portfolio-card-with-hint"
          :portfolio-data="portfolioData"
          :loading="loading"
        >
          <template #hint>
            <hint-popup
              :id="23"
              class="portfolio-card-hint-popup"
            />
          </template>
        </portfolio-history-chart>
      </b-col>
    </b-row>

    <b-row class="mt-0 match-height">
      <b-col cols="6">
        <matured-products-chart
          ref="returnsChart"
          class="portfolio-card-with-hint"
          :returns-data="portfolioData"
          :loading="loading"
        >
          <template #hint>
            <hint-popup
              :id="24"
              class="portfolio-card-hint-popup"
            />
          </template>
        </matured-products-chart>
      </b-col>

      <b-col cols="6">
        <future-maturities-chart
          ref="futureMaturities"
          class="portfolio-card-with-hint"
          :future-maturities-data="portfolioData"
          :loading="loading"
        >
          <template #hint>
            <hint-popup
              :id="25"
              class="portfolio-card-hint-popup"
            />
          </template>
        </future-maturities-chart>
      </b-col>
    </b-row>

    <b-row class="mt-0 match-height">
      <b-col cols="6">
        <payoff-scatter-chart
          ref="payoffScatter"
          :payoff-scatter="portfolioData && portfolioData.state_payoffscatter"
          :portfolio-name="portfolioName"
          :portfolio-id="portfolioType"
          :search-filter="searchFilter"
          :product-weight-mode="productWeightMode"
          :loading="loading"
          @download-csv="$emit('download-csv')"
        >
          <template #header-hint>
            <hint-popup
              :id="26"
              class="mr-50"
            />
          </template>
        </payoff-scatter-chart>
      </b-col>

      <b-col cols="6">
        <levels-scatter-chart
          ref="levelsScatter"
          :levels-scatter-data="portfolioData"
          :portfolio-name="portfolioName"
          :portfolio-id="portfolioType"
          :search-filter="searchFilter"
          :product-weight-mode="productWeightMode"
          :loading="loading"
          @download-csv="$emit('download-csv')"
        >
          <template #header-hint>
            <hint-popup
              :id="27"
              class="mr-50"
            />
          </template>
        </levels-scatter-chart>
      </b-col>
    </b-row>

    <breakdown-charts
      class="mt-0"
      :portfolio-type="portfolioType"
      :product-weight-mode="productWeightMode"
      :search-filter="searchFilter"
    />

    <portfolio-maturities
      class="mt-0"
      :product-weight-mode="productWeightMode"
      :portfolio-type="portfolioType"
      :portfolio-counts="portfolioCounts"
      :search-filter="searchFilter"
      :portfolio-name="portfolioName"
      @download-csv="$emit('download-csv')"
    />

    <portfolio-upcoming-events
      class="mt-0"
      :product-weight-mode="productWeightMode"
      :portfolio-type="portfolioType"
      :portfolio-counts="portfolioCounts"
      :search-filter="searchFilter"
      :portfolio-name="portfolioName"
      @download-csv="$emit('download-csv')"
    />
  </div>
</template>

<script>
import {
  BRow, BCol,
} from 'bootstrap-vue'
import HintPopup from '@/views/components/HintPopup.vue'
import ReportTableCard from '../../components/ReportTableCard.vue'
import PortfolioEvolutionChart from './charts/PortfolioEvolutionChart.vue'
import MaturedProductsChart from './charts/MaturedProductsChart.vue'
import FutureMaturitiesChart from './charts/FutureMaturitiesChart.vue'
import PayoffScatterChart from './charts/PayoffScatterChart.vue'
import LevelsScatterChart from './charts/LevelsScatterChart.vue'
import PortfolioHistoryChart from './charts/PortfolioHistoryChart.vue'
import BreakdownCharts from './charts/BreakdownCharts.vue'
import PortfolioUpcomingEvents from './PortfolioUpcomingEvents.vue'
import PortfolioMaturities from './PortfolioMaturities.vue'

export default {
  components: {
    BRow,
    BCol,
    PortfolioEvolutionChart,
    MaturedProductsChart,
    FutureMaturitiesChart,
    PayoffScatterChart,
    LevelsScatterChart,
    ReportTableCard,
    HintPopup,
    PortfolioHistoryChart,
    BreakdownCharts,
    PortfolioUpcomingEvents,
    PortfolioMaturities,
  },
  props: {
    portfolioType: {
      type: [String, Number],
      default: 'u',
    },
    portfolioData: {
      type: Object,
      default: null,
    },
    evolutionData: {
      type: Object,
      default: null,
    },
    indexUnderlyingVolRatio: {
      type: Number,
      default: null,
    },
    productWeightMode: {
      type: String,
      default: 'equal',
    },
    searchFilter: {
      type: Object,
      default: null,
    },
    portfolioCounts: {
      type: Object,
      default: null,
    },
    portfolioName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    performanceSummaryFields() {
      return this.portfolioData?.state_maturity?.header || []
    },
    performanceSummaryItems() {
      return this.portfolioData?.state_maturity?.content || []
    },
  },
  methods: {
    getReturnsMode() {
      return this.$refs.returnsChart?.selectedMode || 'all'
    },
    collectCharts(productWeightMode) {
      if (this.loading || this.productWeightMode !== productWeightMode
        || this.portfolioData?.selectedmode !== productWeightMode) {
        throw new Error('Portfolio charts do not match the requested weighting mode')
      }
      const chartRefs = {
        portfolioReturnsChart: 'returnsChart',
        portfolioPayoffScatterChart: 'payoffScatter',
        portfolioLevelsScatterChart: 'levelsScatter',
        portfolioFutureMaturitiesChart: 'futureMaturities',
      }
      const charts = {}
      Object.entries(chartRefs).forEach(([key, ref]) => {
        const chart = this.$refs[ref]
        if (!chart || chart.loading) throw new Error('Portfolio chart is not ready')
        const image = chart.getDataURL()
        if (image) charts[key] = image
        else if (!chart.noData) throw new Error('Unable to render portfolio chart')
      })
      return charts
    },
  },
}
</script>

<style scoped>
.portfolio-card-with-hint ::v-deep .card-body {
  position: relative;
}

.portfolio-card-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.portfolio-card-with-hint ::v-deep .card-body > .card-title {
  padding-right: 2.5rem;
}
</style>
